<template>
  <div class="fixed top-0 h-screen font-inter flex flex-col w-[294px] bg-primaryBg text-primaryDark">
    <!-- Header Section -->
    <div class="flex-shrink-0 px-4 mt-6 space-y-4 mb-5">
      <p class="font-semibold text-primaryDark text-[16px]">B2B</p>

      <div class="flex items-center gap-2">
        <div class="flex items-center flex-1 h-9 px-3 gap-2 bg-[#F0F2F5] rounded-lg">
          <svg class="w-4 h-4 text-grayDark flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search for anything"
            class="bg-transparent text-[13px] text-grayDark placeholder-grayDark w-full focus:outline-none"
          />
        </div>

        <div class="flex items-center">
          <button @click="goBack" aria-label="Back"
            class="p-1 rounded-md text-gray-400 hover:text-primaryDarkBg hover:bg-[#F0F2F5] focus:outline-none">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.9998 19.9201L8.47984 13.4001C7.70984 12.6301 7.70984 11.3701 8.47984 10.6001L14.9998 4.08008" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button @click="goForward" aria-label="Forward"
            class="p-1 rounded-md text-gray-400 hover:text-primaryDarkBg hover:bg-[#F0F2F5] focus:outline-none">
            <svg class="w-5 h-5 transform rotate-180" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.9998 19.9201L8.47984 13.4001C7.70984 12.6301 7.70984 11.3701 8.47984 10.6001L14.9998 4.08008" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <hr class="w-[254px] border-t-[1px] border-[#E5E7ED]">
    </div>

    <!-- Scrollable Navigation Section -->
    <div class="flex-1 overflow-y-auto px-4">
      <nav>
        <router-link v-for="item in navigationItems" :key="item.path" :class="[
          'flex items-center w-[254px] h-10 px-3 py-2 gap-2 mb-2 text-[14px] font-medium rounded-lg transition-colors',
          isActiveLink(item.path)
            ? 'bg-[#F0F2F5] text-primaryDarkBg'
            : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
        ]" :to="item.path">
          <component :is="getIconComponent(item.icon)" class="w-5 h-5" />
          {{ item.name }}
        </router-link>
      </nav>
      
      <hr class="w-[254px] border-t-[1px] border-[#E5E7ED] my-4">

      <nav>
        <router-link v-for="item in secondaryItems" :key="item.path" :class="[
          'flex items-center w-[254px] h-10 px-3 py-2 gap-2 mb-2 text-[14px] font-medium rounded-lg transition-colors',
          isActiveLink(item.path)
            ? 'bg-[#F0F2F5] text-primaryDarkBg'
            : 'text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg'
        ]" :to="item.path">
          <component :is="getIconComponent(item.icon)" class="w-5 h-5" />
          {{ item.name }}
        </router-link>
      </nav>
    </div>

    <!-- Bottom Section -->
    <div class="flex-shrink-0 px-4 pb-4">
      <router-link v-for="item in sidebarBottom" :key="item.path" :class="[
        'flex items-center w-[254px] h-10 px-3 py-2 gap-2 text-[14px] font-medium rounded-lg text-grayDark hover:bg-[#F0F2F5] hover:text-primaryDarkBg focus:outline-none focus:shadow-outline',
        isActiveLink(item.path) ? 'bg-[#F0F2F5] text-primaryDarkBg' : ''
      ]" :to="item.path">
        <component :is="getIconComponent(item.icon)" class="w-5 h-5" />
        {{ item.name }}
      </router-link>
    </div>
  </div>
</template>

<script>
import DashboardIcon from './svg-icons/DashboardIcon.vue';
import DashboardActiveIcon from './svg-icons/DashboardActiveIcon.vue';
import ProjectIcon from './svg-icons/ProjectIcon.vue';
import FolderIcon from './svg-icons/FolderIcon.vue';
import TeamsIcon from './svg-icons/TeamsIcon.vue';
import TeamActiveIcon from './svg-icons/TeamActiveIcon.vue';
import TIIcon from './svg-icons/TIIcon.vue';
import TIActiveIcon from './svg-icons/TIActiveIcon.vue';
import UserIcon from './svg-icons/UserIcon.vue';
import UserActiveIcon from './svg-icons/UserActiveIcon.vue';
import ReportIcon from './svg-icons/ReportIcon.vue';
import SettingsIcon from './svg-icons/SettingsIcon.vue';
import SupportIcon from './svg-icons/SupportIcon.vue';
import PricingActiveIcon from './svg-icons/PricingActiveIcon.vue';
import PricingIcon from './svg-icons/PricingIcon.vue';

import BookIcon from './svg-icons/BookIcon.vue';

export default {
  name: 'SidebarComponent',
  components: {
    DashboardIcon,
    DashboardActiveIcon,
    ProjectIcon,
    FolderIcon,
    TeamsIcon,
    TeamActiveIcon,
    TIIcon,
    TIActiveIcon,
    UserIcon,
    UserActiveIcon,
    ReportIcon,
    SettingsIcon,
    SupportIcon,
    PricingActiveIcon,
    PricingIcon,
    BookIcon,
  },
  data() {
    return {
      navigationItems: [
        { name: 'Home', path: '/dashboard', icon: 'dashboard' },
        { name: 'Projects', path: '/projects', icon: 'project' },
        { name: 'Teams', path: '/teams', icon: 'team' },
        { name: 'Pricing', path: '/pricing', icon: 'pricing' },
      ],
      secondaryItems: [
        { name: 'Hire Verified Talent', path: '/talent-pool', icon: 'hire-verified' },
        { name: 'Learning Plans', path: '/learning-plans', icon: 'book' },
      ],
      sidebarBottom: [
        { name: 'Settings', path: '/settings', icon: 'setting' },
        { name: 'Support', path: '/support', icon: 'support' },
      ]
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isInvestor() {
      return this.$store.getters.isInvestor;
    },
  },
  methods: {
    isActiveLink(path) {
      return this.$route.path.startsWith(path);
    },
    getIconComponent(iconName) {
      switch (iconName) {
        case 'dashboard':
          return this.isActiveLink('/dashboard') ? 'DashboardActiveIcon' : 'DashboardIcon';
        case 'project':
          return this.isActiveLink('/projects') ? 'ProjectIcon' : 'FolderIcon';
        case 'team':
          return this.isActiveLink('/teams') ? 'TeamActiveIcon' : 'TeamsIcon';
        case 'pricing':
          return this.isActiveLink('/pricing') ? 'PricingActiveIcon' : 'PricingIcon';
        case 'hire-verified':
          return this.isActiveLink('/talent-pool') ? 'TIActiveIcon' : 'TIIcon';
        case 'book':
          return 'BookIcon';
        case 'setting':
          return 'SettingsIcon';
        case 'support':
          return 'SupportIcon';
        default:
          return 'ReportIcon'; // Default fallback icon
      }
    },
    goBack() {
      if (window && window.history && window.history.length > 1) {
        this.$router.go(-1);
      }
    },
    goForward() {
      this.$router.go(1);
    },
  },
};
</script>